import { GlobalOrderItemsRequest, GlobalOrderResponse, GlobalOrdersRequest, ShipmentDataForSNOrderInDTO, SnPartyConfigDTO } from '@api/ringil-api.v2';
import { Order, OrderItem } from '@components/framework/orders/common.tsx';
import { OrdersConfig } from '@components/framework/orders/validation.ts';
import { SecuredConfig } from '@components/security/securedDecoration';
import { Clause } from '@components/security/types';
import { POLICY_DESCRIPTION } from './snSecurityClauses';


// New-page (framework orders) form data — decoupled from the obsolete `SupplierNotificationOrdersData` so legacy stays on the old stack.
export interface SnFrameworkOrdersData {
  orders: Order[];
  companyCode?: string;
}

// SN order settings (FieldMode) → framework OrdersConfig. cargo + dimensions fixed-required; amount/value/item-note driven by config.
export const generateSnOrderConfig = (config?: SnPartyConfigDTO): OrdersConfig => {
  const amount = config?.amountOfOrder;
  const value = config?.orderValue;
  const note = config?.manipulationUnitNote;
  const stackable = config?.stackable;
  const detailsRequired = !!(amount?.required || value?.required);
  const detailsVisible = !!(amount?.visible || value?.visible);
  return {
    orderName: { visibility: 'always', required: true },
    cargo: { visibility: 'always', required: true },
    cargoDimensions: {
      visibility: 'always',
      required: true,
      items: { width: { required: true }, height: { required: true }, length: { required: true }, weight: { required: true } },
    },
    cargoDetails: {
      visibility: detailsRequired ? 'always' : detailsVisible ? 'optional' : 'never',
      required: false,
      items: {
        orderAmount: { required: !!amount?.required },
        orderValue: { required: !!value?.required },
        orderDescription: { required: false },
      },
    },
    orderTotalWeight: { render: true, required: false },
    unloadingLocationId: { render: false, required: false, visibility: 'never' },
    itemNote: { visibility: note?.visible ? 'always' : 'never' },
    stackable: { visibility: stackable?.visible ? 'always' : 'never' },
  };
};

// --- save: framework Order -> GlobalOrdersRequest (mirrors DAP; carries PAP externalOrders) ---
const mapItem = (item: OrderItem): GlobalOrderItemsRequest => ({
  cargoTemplateCode: item.cargoTemplateCode,
  amount: item.amount,
  stackable: item.stackable,
  length: item.length,
  width: item.width,
  height: item.height,
  weight: item.weight,
  itemNote: item.itemNote,
});

// `externalIdPresent` reflects the combined PAP gate; when false we must not send `externalOrders` (stale prefill from
// `externalData` on edit, or a location that isn't mapped upstream). Omit the field entirely in that case.
export const mapSnOrder = (order: Order, externalIdPresent: boolean = false): GlobalOrdersRequest => ({
  orderName: order.orderName,
  items: (order.items || []).map(mapItem),
  orderValue: order.orderValue,
  orderTotalWeight: order.orderTotalWeight,
  orderAmount: order.orderAmount,
  orderDescription: order.orderDescription,
  externalOrders: externalIdPresent ? order.externalOrders : undefined,
});

// --- load: GlobalOrderResponse (OUT) -> framework Order, for prefill (duplicate, future new-detail) ---
export const globalResponseToFrameworkOrder = (o: GlobalOrderResponse): Order => ({
  orderName: o.orderName ?? '',
  orderAmount: o.orderAmount,
  orderDescription: o.orderDescription,
  orderValue: o.orderValue,
  orderTotalWeight: o.orderTotalWeight,
  externalOrders: o.externalData,
  items: (o.items || []).map(i => ({
    cargoTemplateCode: i.cargoTemplate.code,
    amount: i.amount,
    stackable: i.stackable,
    decimalPlaces: i.cargoTemplate.decimalPlaces,
    height: i.height,
    width: i.width,
    length: i.length,
    weight: i.weight,
    itemNote: i.itemNote,
  })),
});

// --- load: shipment data (OUT) -> framework Order, for prefill when creating an SN out of a shipment ---
export const shipmentDataToFrameworkOrder = (o: ShipmentDataForSNOrderInDTO): Order => ({
  orderName: o.orderName ?? '',
  orderAmount: o.orderAmount,
  orderDescription: o.orderDescription,
  orderValue: o.orderValue ? { price: o.orderValue.amount, currency: o.orderValue.currencyCode } : undefined,
  orderTotalWeight: o.weight?.value,
  items: (o.items || []).map(i => ({
    cargoTemplateCode: i.cargoItemTypeCode,
    amount: i.amount,
    stackable: i.stackable ?? false,
    height: i.height,
    width: i.width,
    length: i.length,
    weight: i.weight,
  })) as OrderItem[],
});

// --- shared `sn-*` test ids (create + detail sidebar) ---
const buildSnTestId = (...parts: Array<string | null | undefined>) => ['sn', ...parts.filter(Boolean)].join('-');

export const buildSnOrderTestId = (orderIndex: number, suffix: string) => {
  const orderPrefix = buildSnTestId(`order${orderIndex + 1}`);
  return suffix === 'id' ? `${orderPrefix}-orderName` : `${orderPrefix}-${suffix}`;
};

export const buildSnManUnitTestId = (orderIndex: number, itemIndex: number, suffix?: string) => {
  const manUnitPrefix = buildSnTestId(`order${orderIndex + 1}`, `manUnit${itemIndex + 1}`);
  switch (suffix) {
    case undefined:
    case '':
      return manUnitPrefix;
    case 'Amount':
      return `${manUnitPrefix}-amount`;
    case 'stackable':
      return `${manUnitPrefix}-stackable`;
    case 'Length':
      return `${manUnitPrefix}-length`;
    case 'Width':
      return `${manUnitPrefix}-width`;
    case 'Height':
      return `${manUnitPrefix}-height`;
    case 'Weight':
      return `${manUnitPrefix}-weight`;
    default:
      return `${manUnitPrefix}-${suffix}`;
  }
};

// --- shared per-field order overlay (create + detail sidebar) ---
// generic OrdersComponent key → the SN policy FieldMode that drives it. `idPrefix` keeps page-distinct overlay ids.
// `writeClause` is the page's SN write permission (create and detail check different ones).
type OrderFieldKey = 'orderAmount' | 'orderValue' | 'itemNote';
export const makeSnOrderFieldSecured =
  (config: SnPartyConfigDTO | undefined, writeClause: Clause, tr: (key: string, def?: string) => string, idPrefix = 'sn-order') =>
  (key: OrderFieldKey, orderIndex: number, itemIndex?: number): SecuredConfig => {
    const mode = key === 'orderAmount' ? config?.amountOfOrder : key === 'orderValue' ? config?.orderValue : config?.manipulationUnitNote;
    return {
      id: `${idPrefix}-${key}`,
      // One order field repeats per order (and per handling unit for the item note): the order index is the instance.
      instance: itemIndex != null ? `${orderIndex}-item-${itemIndex}` : `${orderIndex}`,
      description: `Order field ${key} of a supplier notification order; the customer's notification settings decide whether it is shown and editable`,
      visible: [{ type: 'custom', label: tr('CreateSupplierNotification.policyVisible', 'Visible by company policy'), satisfied: mode?.visible ?? false, description: POLICY_DESCRIPTION.visible, byDesign: false }],
      editable: [
        writeClause,
        { type: 'custom', label: tr('CreateSupplierNotification.policyEditable', 'Editable by company policy'), satisfied: mode?.editable ?? false, description: POLICY_DESCRIPTION.editable, byDesign: false },
      ],
    };
  };
